import Cookies from 'js-cookie';
import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

const ADMIN_EMAIL = 'admin@hdlcc.com';
const MEMBER_EMAIL = 'richard@rstomlinson.net';
const ADMIN_PASSWORD = 'password';
const ADMIN_MFA = '123456';

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const savedUser = Cookies.get('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!Cookies.get('isAuthenticated');
  });
  const [adminEmailVerified, setAdminEmailVerified] = useState(false);

  const checkUserType = async email => {
    // Stub implementation - in production this would be an API call
    if (email.toLowerCase() === ADMIN_EMAIL) return 'admin';
    if (email.toLowerCase() === MEMBER_EMAIL) return 'member';
    return 'member'; // Default to member for demo
  };

  const requestLoginToken = async email => {
    try {
      const userType = await checkUserType(email);

      if (userType === 'member') {
        // Simulate storing the token for verification
        // In production, this would send an email with a login link
        const demoToken = btoa(email);
        localStorage.setItem('pendingLoginToken', demoToken);

        console.log(`[STUB] Login token requested for ${email}`);
        console.log('[STUB] In production, an email would be sent with a login link');
        console.log(`[STUB] Demo token: ${demoToken}`);
      }

      return true;
    } catch (error) {
      console.error('Error requesting login token:', error);
      throw new Error('Failed to send login token');
    }
  };

  const loginAdmin = async (email, password) => {
    // Stub implementation for admin login
    if (email.toLowerCase() !== ADMIN_EMAIL || password !== ADMIN_PASSWORD) {
      throw new Error('Invalid credentials');
    }
    setAdminEmailVerified(true);
    return true;
  };

  const verifyAdminMFA = async code => {
    if (!adminEmailVerified) {
      throw new Error('Email not verified');
    }

    if (code !== ADMIN_MFA) {
      throw new Error('Invalid MFA code');
    }

    const userData = {
      email: ADMIN_EMAIL,
      type: 'admin',
      name: 'Admin User',
    };

    // Set cookies with 1 hour expiry
    const expires = new Date(new Date().getTime() + 60 * 60 * 1000);
    Cookies.set('user', JSON.stringify(userData), { expires });
    Cookies.set('isAuthenticated', 'true', { expires });

    setUser(userData);
    setIsAuthenticated(true);
    setAdminEmailVerified(false);
    return true;
  };

  const verifyLoginToken = async token => {
    try {
      // For demo purposes, verify against stored token
      const storedToken = localStorage.getItem('pendingLoginToken');
      if (token !== storedToken) {
        throw new Error('Invalid token');
      }

      // Decode email from token (base64)
      const email = atob(token);

      const userData = {
        email: email,
        type: 'member',
        name: 'Richard Tomlinson',
        preferences: {
          marketingEmails: true,
          magazineEmails: true,
          renewalEmails: true,
        },
      };

      // Set cookies with 1 hour expiry
      const expires = new Date(new Date().getTime() + 60 * 60 * 1000);
      Cookies.set('user', JSON.stringify(userData), { expires });
      Cookies.set('isAuthenticated', 'true', { expires });

      setUser(userData);
      setIsAuthenticated(true);
      localStorage.removeItem('pendingLoginToken');
      return true;
    } catch (error) {
      console.error('Error verifying login token:', error);
      throw new Error('Invalid or expired login token');
    }
  };

  const updateProfile = async (name, email) => {
    try {
      // In production, this would be an API call
      // await axios.put('/api/user/profile', { name, email });

      const updatedUser = {
        ...user,
        name,
        email,
      };

      // Update cookies
      const expires = new Date(new Date().getTime() + 60 * 60 * 1000);
      Cookies.set('user', JSON.stringify(updatedUser), { expires });

      setUser(updatedUser);
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Failed to update profile');
    }
  };

  const updatePreferences = async preferences => {
    try {
      // In production, this would be an API call
      // await axios.put('/api/user/preferences', preferences);

      const updatedUser = {
        ...user,
        preferences: {
          ...user.preferences,
          ...preferences,
        },
      };

      // Update cookies
      const expires = new Date(new Date().getTime() + 60 * 60 * 1000);
      Cookies.set('user', JSON.stringify(updatedUser), { expires });

      setUser(updatedUser);
      return true;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw new Error('Failed to update preferences');
    }
  };

  const logout = () => {
    // Clear auth state
    Cookies.remove('user');
    Cookies.remove('isAuthenticated');
    setUser(null);
    setIsAuthenticated(false);
    setAdminEmailVerified(false);
    localStorage.removeItem('pendingLoginToken');

    // Redirect based on user type
    navigate('/');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        checkUserType,
        requestLoginToken,
        loginAdmin,
        verifyAdminMFA,
        verifyLoginToken,
        updateProfile,
        updatePreferences,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
